/**
 * ForestBiome - The third biome (1000-1500 miles)
 *
 * Forest characteristics:
 * - Lush woodland descending from mountains
 * - Dense tree coverage, dappled sunlight
 * - Winding roads through towering canopy
 * - Rich green color palette with earth tones
 */

import { BiomeRenderer } from '../BiomeRenderer.js';
import { createBiomeConfig, validateBiomeConfig } from './BiomeConfig.js';

// Forest-specific color palettes for each time of day
// Index: 0=Day, 1=Sunset, 2=Night, 3=Dawn
export const FOREST_COLORS = [
    // ═══════════════════════════════════════════════════════════════════════════
    // DAY - Rich greens and forest floor browns
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Lush forest greens
            0x2d5a2d, 0x3a6b3a, 0x4a7a4a,
            // Forest floor browns
            0x4a4030, 0x5a5040, 0x3a3525,
            // Mossy accents
            0x5a7a4a, 0x4a6a3a, 0x6a8a5a
        ],
        rockColors: [
            0x5a5a50,   // Mossy gray
            0x6a6a60,   // Forest stone
            0x4a4a40,   // Dark rock
            0x7a7a70    // Light boulder
        ],
        shrubColors: [
            0x1a3a1a,   // Deep forest
            0x2a4a2a,   // Dark green
            0x3a5a3a,   // Forest green
            0x254525    // Shadow green
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SUNSET - Golden light filtering through canopy
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Golden-lit foliage
            0x5a7a3a, 0x6a8a4a, 0x4a6a2a,
            // Warm shadows
            0x5a4a30, 0x6a5a40, 0x4a3a20,
            // Amber highlights
            0x7a8a4a, 0x8a9a5a, 0x6a7a3a
        ],
        rockColors: [
            0x6a5a40,   // Warm stone
            0x7a6a50,   // Sunset rock
            0x5a4a30,   // Shadowed
            0x8a7a60    // Golden boulder
        ],
        shrubColors: [
            0x2a3a1a,   // Shadowed tree
            0x3a4a2a,   // Warm green
            0x4a5a3a,   // Golden-green
            0x2a3520    // Deep shadow
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // NIGHT - Deep mysterious forest
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Dark canopy blues
            0x1a2a2a, 0x2a3a3a, 0x0a1a1a,
            // Forest floor shadows
            0x1a1a15, 0x2a2a20, 0x0a0a08,
            // Moonlit patches
            0x2a3a38, 0x3a4a48, 0x1a2a28
        ],
        rockColors: [
            0x2a2a28,   // Night stone
            0x3a3a38,   // Dark rock
            0x1a1a18,   // Deep shadow
            0x4a4a48    // Moonlit rock
        ],
        shrubColors: [
            0x0a1a0a,   // Very dark
            0x1a2a1a,   // Night forest
            0x0a150a,   // Deep shadow
            0x152015    // Dark green
        ]
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // DAWN - Misty morning forest
    // ═══════════════════════════════════════════════════════════════════════════
    {
        terrainColors: [
            // Misty greens
            0x4a6a5a, 0x5a7a6a, 0x3a5a4a,
            // Soft morning earth
            0x5a5a48, 0x6a6a58, 0x4a4a38,
            // Dewy highlights
            0x6a8a6a, 0x7a9a7a, 0x5a7a5a
        ],
        rockColors: [
            0x5a5a58,   // Morning stone
            0x6a6a68,   // Misty rock
            0x4a4a48,   // Cool gray
            0x7a7a78    // Pale boulder
        ],
        shrubColors: [
            0x2a3a2a,   // Misty tree
            0x3a4a3a,   // Soft green
            0x1a2a1a,   // Shadowed
            0x3a4a38    // Cool green
        ]
    }
];

export class ForestBiome extends BiomeRenderer {
    constructor(scene, camera, worldTranslator, config = {}) {
        // Forest-specific configuration
        const forestConfig = createBiomeConfig({
            // ─────────────────────────────────────────────────────────────
            // ROAD - Winding paths through dense woodland
            // ─────────────────────────────────────────────────────────────
            roadWidth: 8,                        // Narrow forest road
            chunkLength: 50,
            pathSegmentLength: 120,              // Tighter curves
            pathLookAhead: 80,
            roadMaxLateralOffset: 180,           // Winding through trees
            roadMaxVerticalOffset: 25,           // Gentle hills
            roadSplineTension: 0.0,

            // ─────────────────────────────────────────────────────────────
            // TERRAIN - Rolling woodland hills
            // ─────────────────────────────────────────────────────────────
            terrainColor: 0x336638,              // Forest green
            terrainAmplitudeLarge: 120,          // Gentle hills
            terrainAmplitudeMedium: 60,          // Rolling terrain
            terrainAmplitudeSmall: 25,           // Forest floor undulation
            terrainAmplitudeFine: 8,             // Surface detail
            terrainHeightMultiplier: 0.8,        // Lower overall height
            waterLevel: -15,                     // Forest streams

            // ─────────────────────────────────────────────────────────────
            // TERRAIN DIMENSIONS
            // ─────────────────────────────────────────────────────────────
            terrainWidth: 1200,
            terrainSegmentsOut: 18,
            terrainSegmentsAlong: 1,

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE STRIP
            // ─────────────────────────────────────────────────────────────
            roadsideStripWidth: 40,
            roadsideStripSegments: 4,
            roadsideStripOverlap: 0.05,
            mountainOverlapIntoStrip: 8,

            // ─────────────────────────────────────────────────────────────
            // TERRAIN FEATURES - Gentle woodland terrain
            // ─────────────────────────────────────────────────────────────
            terrainFeaturesEnabled: true,
            terrainFeatureScale: 0.00004,
            terrainFeatureWeights: {
                dunes: 0.1,       // Rare
                peaks: 0.3,       // Occasional hills
                plateaus: 0.6,    // Flat clearings
                spires: 0.1,      // Rare
                cliffs: 0.3,      // Some ravines
                valleys: 0.9      // Forest valleys
            },
            terrainFeatureBlend: 0.6,

            // ─────────────────────────────────────────────────────────────
            // TERRAIN HEIGHT LAYERING
            // ─────────────────────────────────────────────────────────────
            terrainLayerHeights: [0.3, 0.5, 0.8, 1.2],
            terrainLayerDistances: [0.1, 0.3, 0.6, 1.0],

            // ─────────────────────────────────────────────────────────────
            // FLAT ZONE - Trees close to road
            // ─────────────────────────────────────────────────────────────
            terrainFlatZoneMin: 0.01,
            terrainFlatZoneMax: 0.12,
            terrainBlendRange: 0.1,

            // ─────────────────────────────────────────────────────────────
            // ROADSIDE OBJECTS - Dense forest trees
            // ─────────────────────────────────────────────────────────────
            enableRoadsideObjects: true,
            roadsideObjectSpawnDistance: 600,
            roadsideObjectDensity: 0.7,          // Dense forest
            roadsideObjectMinSafeZone: 8,
            roadsideObjectDistributionWidth: 150,
            rockProbability: 0.2,                // Few rocks, mostly trees
            rockMinSize: 2,
            rockMaxSize: 5,
            shrubMinSize: 8,                     // Tall trees
            shrubMaxSize: 22,
            shrubSpriteCount: 3,
            shrubScaleVariation: 0.35,
            shrubStretchVariation: 0.2,
            roadsideObjectFadeInDuration: 0.5,

            // Forest sprites (using shrubs as placeholder)
            shrubSpritePaths: [
                'assets/sprites/shrub1.png',
                'assets/sprites/shrub2.png',
                'assets/sprites/shrub3.png'
            ],

            // Merge with any user-provided config
            ...config
        });

        // Validate configuration in development
        validateBiomeConfig(forestConfig, 'ForestBiome');

        super(scene, camera, worldTranslator, forestConfig);

        // Biome identity
        this.biomeName = 'Forest';
        this.minMiles = 1000;
        this.maxMiles = 1500;

        // Store biome-specific color palettes
        this.biomeColors = FOREST_COLORS;
        this.palette.setBiomeColors(FOREST_COLORS);
    }

    getTerrainColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.terrainColors : [];
    }

    getRockColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.rockColors : [];
    }

    getShrubColors(timeIndex) {
        const palette = this.biomeColors[timeIndex];
        return palette ? palette.shrubColors : [];
    }
}

export const FOREST_BIOME_ENTRY = {
    minMiles: 1000,
    maxMiles: 1500,
    BiomeClass: ForestBiome,
    name: 'Forest'
};
